import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';

export interface DropdownOption {
    [key: string]: any;
}

@Component({
    selector: 'app-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.scss'],
    standalone: false,
})
export class DropdownComponent implements OnChanges {
    @ViewChild('container') container!: ElementRef;

    @Input() name = '';
    @Input() label = '';
    @Input() itemList: DropdownOption[] = [];
    @Input() displayKey = '';
    @Input() valueKey = '';
    @Input() value: any = '';
    @Input() dark = false;
    @Input() height = 0;
    @Input() isDisabled = false;
    @Input() isMultipleSelection = false;
    @Output() inputChange = new EventEmitter<any>();

    inputValue = '';
    isShowSelect = false;

    selectedValues: DropdownOption[] = [];

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent) {
        if (!this.container?.nativeElement.contains(event.target) && this.isShowSelect) {
            this.isShowSelect = false;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['value'] || changes['itemList']) {
            this.updateSelectedValues();
            this.updateInputValue();
        }
    }

    openSelect() {
        if (this.isDisabled) return;

        this.isShowSelect = !this.isShowSelect;
    }

    selectValue(event: MouseEvent, item: DropdownOption) {
        event.preventDefault();
        event.stopPropagation();

        if (this.isDisabled) return;

        if (this.isMultipleSelection) {
            this.toggleMultipleSelection(item);
            return;
        }

        this.selectedValues = [item];
        this.updateInputValue();

        this.inputChange.emit({
            name: this.name,
            value: item,
        });

        this.isShowSelect = false;
    }

    isSelected(item: DropdownOption): boolean {
        return this.selectedValues.some(selectedItem => this.getValue(selectedItem) === this.getValue(item));
    }

    getDisplayValue(item: DropdownOption): string {
        if (!this.displayKey) {
            return String(item);
        }

        return item?.[this.displayKey] ?? '';
    }

    private getValue(item: DropdownOption): any {
        if (!this.valueKey) {
            return item;
        }

        return item?.[this.valueKey];
    }

    private toggleMultipleSelection(item: DropdownOption) {
        if (this.isSelected(item)) {
            this.selectedValues = this.selectedValues.filter(
                selectedItem => this.getValue(selectedItem) !== this.getValue(item)
            );
        } else {
            this.selectedValues = [...this.selectedValues, item];
        }

        this.updateInputValue();

        this.inputChange.emit({
            name: this.name,
            value: this.selectedValues,
        });
    }

    private updateSelectedValues() {
        if (this.isMultipleSelection) {
            this.selectedValues = Array.isArray(this.value) ? [...this.value] : [];

            return;
        }

        this.selectedValues = this.value ? [this.value] : [];
    }

    private updateInputValue() {
        if (!this.selectedValues.length) {
            this.inputValue = '';
            return;
        }

        if (this.isMultipleSelection) {
            const firstItem = this.getDisplayValue(this.selectedValues[0]);
            const additionalCount = this.selectedValues.length - 1;

            this.inputValue = additionalCount > 0 ? `${firstItem} (+${additionalCount})` : firstItem;

            return;
        }

        this.inputValue = this.getDisplayValue(this.selectedValues[0]);
    }
}
